<template>
    <div class="relative flex flex-1 gap-2 sm:gap-4 items-center">
        <SearchModules />
        <div class="relative flex items-center w-3/4 sm:w-2/3 md:w-1/2">
            <SearchPopover
                v-if="!globalSearchStore.connected || (globalSearchStore.connected !== searchModule?.name)"
                ref="searchPopover" />
            <div class="flex-1 grow">
                <CoreInput
                    ref="searchInputRef"
                    v-model="searchInput"
                    :placeholder="searchInputPlaceholder"
                    name="global_search_input"
                    input-classes="block appearance-none placeholder:text-neutral-300 border-none bg-transparent! rounded-sm h-full w-full border-0 py-0 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-0 sm:text-sm focus:ring-0 focus:outline-hidden focus:shadow-outline"
                    @click="handleInputActive"
                    @keydown="handleKeyInput" />
            </div>

            <!-- Anzeige der Tastenkombination -->
            <span
                v-if="useKeymap('meta_k')"
                class="px-2 text-sm text-gray-400">{{ useKeymap('meta_k') }}</span>

            <Icon
                v-if="searchInput"
                name="x-mark"
                class="cursor-pointer inset-y-0 left-0 h-full w-5 text-gray-400"
                aria-hidden="true"
                @click="searchInput = null" />
            <Icon
                v-else
                name="magnifying-glass"
                class="pointer-events-none inset-y-0 left-0 h-full w-5 text-gray-400"
                aria-hidden="true" />
        </div>
    </div>
</template>

<script setup>
import {useTableFeatures} from '@/composables/useTableFeatures'
import {appModules} from "@/utils/appModules"
import SearchModules from '~/components/layouts/GlobalSearch/SearchModules.vue'
import {useGlobalSearchStore} from '~/store/globalSearch'
import {useLayoutStore} from '~/store/layout'

const searchPopover = ref(null)
const globalSearchStore = useGlobalSearchStore()
const layoutStore = useLayoutStore()
const {t: $t} = useI18n()

const searchInputRef = ref(null)

const layoutModule = computed(() => {
    return layoutStore?.currentModule ?? null
})

const searchModule = computed(() => {
    return appModules[globalSearchStore.currentModule] ?? null
})
const previousPageSearchModule = ref(null)

const tableFeatures = computed(() => {
    if (!searchModule?.value?.name || !globalSearchStore.connected) return null
    return useTableFeatures(searchModule.value.name)
})

const route = useRoute()
const router = useRouter()

useShortcuts({
    'meta_k': () => {
        if (searchInputRef?.value?.input) {
            searchInputRef?.value?.input?.focus()
            handleInputActive()
        }
    }
})

const searchInput = computed({
    get: () => globalSearchStore.searchInput,
    set: (value) => {
        // set input in store and in table listing
        globalSearchStore.setSearchInput(value)
        const tableListing = tableFeatures?.value?.tableStore?.listing ?? null
        // save the search input only if the module is the same as the current layout module
        if (searchModule?.value?.name === contextModule.value) {
            if (tableListing && (tableListing.search || value) && tableListing.search !== value) {
                tableFeatures.value.tableStore.listing.search = value
            }
        }
        if (route.query?.search && route.query?.search !== value) {
            // remove search query from route on new input
            const url = new URL(window.location.href)
            url.searchParams.delete('search')
            router.replace({path: url.pathname, query: Object.fromEntries(url.searchParams)})
        }
    }
})

const searchInputPlaceholder = computed(() => {
    if (!searchModule?.value?.name) return $t('globalSearch.defaultPlaceholder')
    return $t('globalSearch.modulePlaceholder', {module: $t(searchModule.value.name + '.table.title')})
})

function handleInputActive() {
    searchPopover?.value?.searchPopoverTrigger?.$el?.click()
}

function handleKeyInput(event) {
    if (event.key === 'Backspace' && !searchInput.value) {
        globalSearchStore.setCurrentModule(null)
        nextTick(() => {
            // TODO fix check if popover is open
            handleInputActive()
        })
    }
}

const contextModule = computed(() => {
    if (globalSearchStore.connected && appModules[globalSearchStore.connected]?.search) {
        return globalSearchStore.connected
    }
    if (layoutModule.value && appModules[layoutModule.value]?.search) {
        return layoutModule.value
    }
    return null
})

/* ********************
 * Handling input value on route change
 * ********************/

watch(() => contextModule.value, (value) => {
    setModuleIfPossible(value)
}, {immediate: true})


function setModuleIfPossible(module) {
    if (module && !!appModules[module]?.search && (!appModules[module]?.search?.ability || useNuxtApp().$can(appModules[module].search.ability[0], appModules[module].search.ability[1]))) {
        globalSearchStore.setCurrentModule(module)
    } else {
        globalSearchStore.setCurrentModule(null)
    }
}

watch(() => route.path, () => {
    if (route.query?.search) {
        searchInput.value = route.query?.search ?? null
        return
    }

    if (searchModule.value?.name !== previousPageSearchModule.value?.name) {
        const tableListing = tableFeatures?.value?.tableStore?.listing ?? null
        if (tableListing && tableListing.search) {
            searchInput.value = tableListing.search
        } else {
            searchInput.value = null
        }
    }

    previousPageSearchModule.value = searchModule.value
})

watch(() => tableFeatures?.value?.tableStore?.listing, () => {
    // set search input from table listing if it exists
    const tableListing = tableFeatures?.value?.tableStore?.listing ?? null
    if (tableListing && tableListing.search) searchInput.value = tableListing.search
}, {immediate: true})

onMounted(() => {
    if (useRoute().query?.search) {
        nextTick(() => {
            searchInput.value = useRoute().query?.search ?? null
        })
    }

    previousPageSearchModule.value = searchModule.value
})
</script>
